import { IframeMessageError } from '@/utils/error-handling-service.js';

/* Private */

const IFRAME_MAX_WAITING_TIME = 5000;

type Handler = (event: MessageEvent) => void;
interface MessagePayload {
	action: string;
	async?: boolean;
	value?: Record<string, unknown>;
}
type MessagePayloadOrEncryptedMessage = MessagePayload | string;
type AsyncMessagePayload = MessagePayload & { async: true };
type AsyncMessagePayloadOrEncryptedMessage = AsyncMessagePayload | string;

/* Public */

const sendMessageToParent: (payload: MessagePayloadOrEncryptedMessage, port?: MessagePort) => boolean = (payload, port) => {
	if (!window.parent || window.parent === window) {
		return false;
	}
	if (port) {
		window.parent.postMessage(payload, '*', [port]);
	} else {
		window.parent.postMessage(payload, '*');
	}
	return true;
};

interface ValidAsyncMessageResponse extends Record<string, unknown> {
	result: Record<string, unknown>;
}
interface ErrorAsyncMessageResponse extends Record<string, unknown> {
	error: Record<string, unknown>;
}
type AsyncMessageResponse = ValidAsyncMessageResponse | ErrorAsyncMessageResponse;

/**
 * Send a message to the parent window and wait for a response.
 * @param payload The message to send. - If it's a string, it will be sent as is. (probably encrypted) - If it's an object, it will be sent as a message with the `async` property set to `true`.
 * @returns A promise that will resolve with the response from the parent window.
 */
const sendAsyncMessageToParent = (payload: AsyncMessagePayloadOrEncryptedMessage) =>
	new Promise((resolve, reject) => {
		const channel = new MessageChannel();

		const timeoutCleaner = setTimeout(() => {
			channel.port1.close();
			reject(new IframeMessageError('Iframe async message timeout'));
		}, IFRAME_MAX_WAITING_TIME);

		channel.port1.onmessage = ({ data }: { data: AsyncMessageResponse }) => {
			clearTimeout(timeoutCleaner);
			channel.port1.close();
			if (data.error) {
				reject(data.error);
			} else {
				resolve(data.result);
			}
		};

		const payloadToSend = typeof payload === 'string' ? payload : { ...payload, async: true };
		sendMessageToParent(payloadToSend, channel.port2);
	});

const addListeners: (handler: Handler) => void = (handler) => {
	window.addEventListener('message', handler);
};
const removeListener: (handler: Handler) => void = (handler) => {
	window.removeEventListener('message', handler);
};

function inIframe() {
	// @ts-ignore
	if (window.Cypress) {
		// Don't false positive detect in Cypress.
		return false;
	}
	try {
		return window.self !== window.top;
	} catch (e) {
		return true;
	}
}

const IframeService = {
	sendMessageToParent,
	sendAsyncMessageToParent,
	addListeners,
	removeListener,
	inIframe,
};

export default IframeService;
